import { z } from 'zod';
import { DATE_PRESETS } from '@/components/dashboard/date-range-filter';

/**
 * URL search-param schema for the dashboard route. Keeping the date
 * window in the URL means:
 *   - Browser back from a drilldown restores the same window the user
 *     had picked, instead of resetting to "Last 30 days".
 *   - The window is shareable: paste a dashboard URL into Slack and
 *     the recipient sees the same scope.
 *   - Drilldown links can carry the window forward to /tasks via
 *     dashFrom/dashTo/dashPreset and reconstruct the dashboard URL on
 *     "Back to dashboard."
 *
 * `preset` is also persisted (not just derived from `dateFrom`/`dateTo`)
 * so the picker can keep highlighting "Last 7 days" instead of falling
 * to "Custom" when the user reloads the page.
 *
 * Validation:
 *  - `dateFrom`/`dateTo` are validated as ISO datetimes at the URL
 *    boundary so a pasted `?dateFrom=garbage` is filtered before it
 *    reaches the API (which would 422 the request anyway).
 *  - `preset` is enumerated against the actual preset catalog so a
 *    typo'd `?preset=foo` falls back to the default rather than
 *    rendering "vs prior period" with no recognized window.
 */
const PRESET_LABELS = DATE_PRESETS.map((p) => p.label);

export const dashboardSearchSchema = z.object({
  /** ISO start instant. Falls back to 30 days ago when absent. */
  dateFrom: z.string().datetime().optional().catch(undefined),
  /** ISO end instant. Falls back to "now" when absent. */
  dateTo: z.string().datetime().optional().catch(undefined),
  /**
   * Preset label as picked in the date filter. Falls back to
   * "Last 30 days" when absent or unrecognized.
   */
  preset: z
    .enum(PRESET_LABELS as [string, ...string[]])
    .optional()
    .catch(undefined),
});

export type DashboardSearch = z.infer<typeof dashboardSearchSchema>;
